import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';


import {DownloadButton} from 'src/components/form-buttons';
import {ToolsRibbonComponent} from 'src/components/tools-ribbon';
import {MAX_SELECTED_ENTITIES} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {
    AccountSearchDownloadTool,
    AccountSearchEditColumns,
    SELECTION_KEY,
    accountSearchResultResultSelector,
} from 'src/modules/account-search-result';

import {Group} from '@pb/kit-component-group';

import {AccountsAdvancedSearchBulkActions} from './bulk-actions/accounts-advanced-search-bulk-actions';

import type {ChildrenProps as AccountSearchControllerProps} from 'src/modules/account-search-result/account-search-result.controller';
import type {UserSearchTemplate} from 'src/openapi/admin/models/UserSearchTemplate';

type Props = {
    getAllEntityIds: AccountSearchControllerProps['getAllEntityIds'],
    place: string,
    adminId: number,
    isLoading: boolean,
    templates: UserSearchTemplate[],
    activeSearchTemplate: UserSearchTemplate,
    setActiveSearchTemplate: (actionProps: UserSearchTemplate) => Promise<string | null>,
    loadTemplates: () => void,
    loadActiveSearchTemplate: () => Promise<void>,
    refreshResults: () => void,
};

export const AccountsSearchToolsRibbon = (props: Props) => {
    const {searchCriteriaDownload} = useEventTracking();

    const result = useSelector(accountSearchResultResultSelector);
    const rows = useMemo(() => (
        result?.data.map(({accountId}) => accountId) || []
    ), [result]);
    const totalCount = useMemo(() => result?.totalCount || 0, [result]);

    const {hasAccess} = useAdminAccessRight();

    return (
        <ToolsRibbonComponent {...{
            selectionKey: SELECTION_KEY,
            rows,
            totalCount,
            extraTools: ids => (
                <Group>
                    <AccountSearchEditColumns
                        place={props.place}
                        adminId={props.adminId}
                        isLoading={props.isLoading}
                        templates={props.templates}
                        activeSearchTemplate={props.activeSearchTemplate}
                        setActiveSearchTemplate={props.setActiveSearchTemplate}
                        loadTemplates={props.loadTemplates}
                        loadActiveSearchTemplate={props.loadActiveSearchTemplate}
                    />
                    {hasAccess(ACCESS_RIGHTS.USER_SCREEN_VIEW) ? (
                        <AccountSearchDownloadTool
                            accountIds={ids}
                            getSortedEntityIds={props.getAllEntityIds}>
                            {handler => (
                                <DownloadButton
                                    disabled={!ids.length}
                                    handler={() => {
                                        searchCriteriaDownload({viewType: 'Account'});
                                        handler();
                                    }}
                                />
                            )}
                        </AccountSearchDownloadTool>
                    ) : null}
                </Group>
            ),
            maxSelectedEntities: MAX_SELECTED_ENTITIES,
            getAllEntityIds: props.getAllEntityIds,
        }}>
            {ids => (
                <AccountsAdvancedSearchBulkActions
                    ids={ids as number[]}
                    onDone={props.refreshResults}
                />
            )}
        </ToolsRibbonComponent>
    );
};
